import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as jobTitles from '../../job-titles'

import * as gql from './gql'
import type { TypeParams } from './types'

/** Responsible persons API for fetching space record responsible persons */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link jobTitles.Collection}*/
  private readonly _jobTitlesEc = ngCore.inject(jobTitles.Collection)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(context => {
    if (!context) {
      return rxjs.of([])
    }

    return gql.queries.recordResponsiblePersons.executor({ ...context })
  })

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    if (!context) {
      return rxjs.of(undefined)
    }
    return gql.mutations.addRecordResponsiblePersons.executor({
      ...context,
      employeeIds: input.employeeIds,
      primaryResponsiblePersonId: input.primaryEmployeeId,
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id: employeeId, updates: input },
  ) => {
    if (!context) {
      return rxjs.of(undefined)
    }
    return gql.mutations.updateRecordResponsiblePerson.executor({
      ...context,
      employeeId,
      input: {
        isPrimary: TypeAsserter.validateButJustWarn(
          input.isPrimary,
          TypeAsserter.assertIsNotNullish,
        ),
      },
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    if (!context) {
      return rxjs.of(undefined)
    }
    return gql.mutations.removeRecordResponsiblePerson.executor({
      ...context,
      employeeId: id,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    this._jobTitlesEc.plugins.cachePlugin.feature.selectors.lookup.asObservable.pipe(
      rxjs.map(
        jobTitleLookup =>
          [
            {
              getValueFrom: person => person.employee.name,
              id: 'name',
              name: 'Name',
            },
            {
              getValueFrom: person =>
                person.employee.jobTitleId &&
                jobTitleLookup[person.employee.jobTitleId]?.name,
              id: 'jobTitle',
              name: 'Title',
            },
            ec.models.Attribute.fromKey<TypeParams>('isPrimary', {
              name: 'Is Primary?',
            }),
          ] satisfies ec.models.Attribute<TypeParams>[],
      ),
    )
}
